import { type Dispatch, type SetStateAction, useState } from "react";
import { useMutation, useQuery } from "@apollo/client/react";

import { useWorkspace } from "src/contexts/global/WorkspaceContext";
import { useClockNow } from "src/pages/WorkforceGuardrails/clockHours";
import {
  refusalReasonsOf,
  type RefusalReason,
} from "src/pages/WorkforceGuardrails/ruleShape";
import { getWorkspaceWorkforceGuardrails } from "src/pages/Workspaces/components/WorkspaceConfig/WorkforceGuardrailsConfig.graphql";
import { inbox } from "src/inbox/inbox.graphql";

import { withCheckLabels } from "./guardrailRuleLabels";
import {
  respondWorkforceGuardrailApprovalStep,
  setWorkforceGuardrailRequestorReason,
  undoWorkforceGuardrailApprovalStep,
} from "./requisitionGuardrailApproval.graphql";
import type {
  ApprovalStep,
  GuardrailCheck,
  GuardrailDecision,
  GuardrailEvaluation,
  GuardrailResult,
} from "./RequisitionGuardrailOutcome";

export type ApprovalResponse = "APPROVE" | "REFUSE";

export type ChainWriteOutcome = ApprovalResponse | "REASON";

export type DecisionInput = {
  reasonIds?: string[];
  comment?: string;
};

export type RefusalCatalog = {
  ready: boolean;
  reasons: RefusalReason[];
};

export type ChainActions = {
  saving: boolean;
  nowMs: number;
  steps: ApprovalStep[];
  catalog: RefusalCatalog;
  reasonText: string | null;
  setReasonText: Dispatch<SetStateAction<string | null>>;
  saveReason: (reason: string) => void;
  openReasonModal: () => void;
  undo: (order: number, approverGroupId: string) => void;
  respondTo: (
    order: number,
    approverGroupId: string,
    response: ApprovalResponse,
    input: DecisionInput
  ) => void;
  readOnly?: boolean;
};

const REFRESH_ERROR = "Could not refresh the approval steps — try again";
const MISSING_ID = "This hiring request has no id yet.";
const MISSING_REASON = "Enter the exception reason.";

const isGuardrailDecision = (value: unknown): value is GuardrailDecision =>
  value === "PASS" || value === "HARD_BLOCK";

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === "object";

const readField = (record: Record<string, unknown>, key: string) => {
  if (key in record) {
    return record[key];
  }
  return undefined;
};

const fieldOf = (value: unknown, key: string) => {
  if (isRecord(value)) {
    return readField(value, key);
  }
  return undefined;
};

const mutationData = (value: unknown) => fieldOf(value, "data");

const resultOf = (data: unknown) => fieldOf(data, "result");

const evaluationPresent = (next: Record<string, unknown>) =>
  "guardrailEvaluation" in next && Boolean(next["guardrailEvaluation"]);

const isWrittenResult = (next: unknown): next is GuardrailResult =>
  isRecord(next) && evaluationPresent(next);

const evaluationOf = (shown: GuardrailResult | null) =>
  shown?.guardrailEvaluation;

const relabelCheck =
  (prior: Map<string, GuardrailCheck>) =>
  (check: GuardrailCheck): GuardrailCheck => {
    const label = prior.get(check.ruleId);
    if (label) {
      return withCheckLabels(check, label);
    }
    return check;
  };

const relabeledEvaluation = (
  written: GuardrailResult,
  checks: GuardrailCheck[],
  prior: Map<string, GuardrailCheck>
): GuardrailResult => {
  const evaluation = evaluationOf(written);
  if (evaluation) {
    return {
      ...written,
      guardrailEvaluation: {
        ...evaluation,
        checks: checks.map(relabelCheck(prior)),
      },
    };
  }
  return written;
};

const mergeLabels = (
  written: GuardrailResult,
  prior: Map<string, GuardrailCheck>
): GuardrailResult => {
  const checks = evaluationOf(written)?.checks;
  if (checks) {
    return relabeledEvaluation(written, checks, prior);
  }
  return written;
};

const labelMap = (shown: GuardrailResult | null) =>
  new Map(
    (evaluationOf(shown)?.checks ?? []).map((check) => [check.ruleId, check])
  );

const shownResult = (
  result: GuardrailResult | null | undefined,
  override: GuardrailResult | null
) => override ?? result ?? null;

const decisionOf = (shown: GuardrailResult | null) =>
  evaluationOf(shown)?.decision ?? shown?.guardrailDecision;

const sortedSteps = (steps: ApprovalStep[] | null | undefined) =>
  [...(steps ?? [])].sort((left, right) => left.order - right.order);

const stepsForDecision = (
  decision: GuardrailDecision,
  evaluation: GuardrailEvaluation | null | undefined
) => {
  if (decision === "HARD_BLOCK") {
    return [];
  }
  return sortedSteps(evaluation?.steps);
};

const messageOf = (error: unknown): string =>
  error instanceof Error ? error.message : "The save failed.";

const isLoading = (state: { loading?: boolean } | undefined) =>
  Boolean(state?.loading);

const needsId = (id: string) => id.length === 0;

const whenIdentified = (
  id: string,
  fail: (message: string) => void,
  run: () => void
) => {
  if (needsId(id)) {
    fail(MISSING_ID);
    return;
  }
  run();
};

const submitReason = (
  text: string,
  fail: (message: string) => void,
  run: () => void
) => {
  if (text.length === 0) {
    fail(MISSING_REASON);
    return;
  }
  run();
};

const applyMutationData =
  (apply: (data: unknown) => void) => (value: unknown) => {
    apply(mutationData(value));
  };

const settleWrite = (
  run: () => Promise<unknown>,
  apply: (data: unknown) => void,
  fail: (error: unknown) => void
) => {
  void Promise.resolve(run()).then(applyMutationData(apply)).catch(fail);
};

const writeOutcome = (
  data: unknown,
  shown: GuardrailResult | null
): GuardrailResult | null => {
  const next = resultOf(data);
  if (isWrittenResult(next)) {
    return mergeLabels(next, labelMap(shown));
  }
  return null;
};

const isReasonStep = (step: ApprovalStep) => step.kind === "reason";

const isPending = (step: ApprovalStep) => step.status === "PENDING";

const draftReason = (reasonText: string | null, step: ApprovalStep) =>
  reasonText ?? step.reason ?? "";

type GuardrailsDocumentQuery = {
  workspace?: { config?: { workforceGuardrails?: unknown } | null } | null;
};

const useRefusalCatalog = (): RefusalCatalog => {
  const workspace = useWorkspace() as { id?: string } | undefined;
  const workspaceId = workspace?.id ?? "";
  const skipped = workspaceId === "";
  const { data, loading, error } = useQuery<GuardrailsDocumentQuery>(
    getWorkspaceWorkforceGuardrails,
    {
      skip: skipped,
      variables: { id: workspaceId },
    }
  );
  const document = data?.workspace?.config?.workforceGuardrails;
  if (skipped || loading || error != null || document == null) {
    return { ready: false, reasons: [] };
  }
  return { ready: true, reasons: refusalReasonsOf(document) };
};

export type ChainWriteHandlers = {
  onRespondStart?: () => void;
  onWritten?: (written: GuardrailResult, response: ChainWriteOutcome) => void;
  onRespondError?: () => void;
};

export const useGuardrailChainActions = (
  result: GuardrailResult | null | undefined,
  readOnly = false,
  refusalReasons?: RefusalReason[],
  writeHandlers?: ChainWriteHandlers
) => {
  const refreshInbox = { refetchQueries: [inbox] };
  const [setReason, reasonState] = useMutation(
    setWorkforceGuardrailRequestorReason,
    refreshInbox
  );
  const [respond, respondState] = useMutation(
    respondWorkforceGuardrailApprovalStep,
    refreshInbox
  );
  const [undoStep, undoState] = useMutation(
    undoWorkforceGuardrailApprovalStep,
    refreshInbox
  );
  const loadedCatalog = useRefusalCatalog();
  const catalog: RefusalCatalog = refusalReasons
    ? { ready: true, reasons: refusalReasons }
    : loadedCatalog;
  const nowMs = useClockNow();
  const incoming = result?.guardrailEvaluation;
  const incomingIdentity = `${result?.id ?? ""}:${incoming?.evaluatedAt ?? ""}:${incoming?.decision ?? ""}`;
  const [tracked, setTracked] = useState(incoming);
  const [trackedIdentity, setTrackedIdentity] = useState(incomingIdentity);
  const [override, setOverride] = useState<GuardrailResult | null>(null);
  const [reasonText, setReasonText] = useState<string | null>(null);
  const [reasonModalOpen, setReasonModalOpen] = useState(false);
  const [reasonModalError, setReasonModalError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  if (tracked !== incoming) {
    setTracked(incoming);
    if (trackedIdentity !== incomingIdentity) {
      setTrackedIdentity(incomingIdentity);
      setOverride(null);
      setReasonText(null);
      setReasonModalOpen(false);
      setReasonModalError(null);
    }
  }
  const shown = shownResult(result, override);
  const decisionValue = decisionOf(shown);
  const decision = isGuardrailDecision(decisionValue) ? decisionValue : null;
  const steps =
    decision == null ? [] : stepsForDecision(decision, evaluationOf(shown));
  const checks = evaluationOf(shown)?.checks ?? [];
  const requisitionId = shown?.id ?? "";
  const saving =
    isLoading(reasonState) || isLoading(respondState) || isLoading(undoState);
  const applyWrite = (data: unknown, response?: ChainWriteOutcome) => {
    const written = writeOutcome(data, shown);
    if (written) {
      const next =
        result?.openedDate != null && written.openedDate == null
          ? { ...written, openedDate: result.openedDate }
          : written;
      setOverride(next);
      setReasonText(null);
      setReasonModalOpen(false);
      setReasonModalError(null);
      setActionError(null);
      if (response != null) {
        writeHandlers?.onWritten?.(written, response);
      }
      return;
    }
    if (response != null) {
      writeHandlers?.onRespondError?.();
    }
    if (reasonModalOpen) {
      setReasonModalError(REFRESH_ERROR);
    } else {
      setActionError(REFRESH_ERROR);
    }
  };
  const pendingReasonStep = steps.find(
    (step) => isReasonStep(step) && isPending(step)
  );
  const openReasonModal = () => {
    if (!pendingReasonStep) {
      return;
    }
    setReasonModalError(null);
    setActionError(null);
    setReasonText(draftReason(reasonText, pendingReasonStep));
    setReasonModalOpen(true);
  };
  const closeReasonModal = () => {
    setReasonModalOpen(false);
    setReasonModalError(null);
  };
  const onReasonDraft = (value: string) => {
    setReasonModalError(null);
    setReasonText(value);
  };
  const runStep = (
    run: () => Promise<unknown>,
    response?: ApprovalResponse
  ) => {
    if (readOnly) {
      return;
    }
    setActionError(null);
    whenIdentified(requisitionId, setActionError, () => {
      if (response != null) {
        writeHandlers?.onRespondStart?.();
      }
      settleWrite(
        run,
        (data) => applyWrite(data, response),
        (error) => {
          writeHandlers?.onRespondError?.();
          setActionError(messageOf(error));
        }
      );
    });
  };
  const undo = (order: number, approverGroupId: string) => {
    runStep(() =>
      Promise.resolve(
        undoStep({ variables: { requisitionId, order, approverGroupId } })
      )
    );
  };
  const respondTo = (
    order: number,
    approverGroupId: string,
    response: ApprovalResponse,
    input: DecisionInput
  ) => {
    runStep(
      () =>
        Promise.resolve(
          respond({
            variables: {
              requisitionId,
              order,
              approverGroupId,
              response,
              ...(input.reasonIds ? { reasonIds: input.reasonIds } : {}),
              ...(input.comment ? { comment: input.comment } : {}),
            },
          })
        ),
      response
    );
  };
  const saveReason = (reason: string) => {
    if (readOnly) {
      return;
    }
    const text = reason.trim();
    setActionError(null);
    const failReason = (message: string) => {
      if (reasonModalOpen) {
        setReasonModalError(message);
      } else {
        setActionError(message);
      }
    };
    whenIdentified(requisitionId, failReason, () => {
      submitReason(text, failReason, () => {
        setReasonModalError(null);
        writeHandlers?.onRespondStart?.();
        settleWrite(
          () =>
            Promise.resolve(
              setReason({ variables: { requisitionId, reason: text } })
            ),
          (data) => applyWrite(data, "REASON"),
          (error) => {
            writeHandlers?.onRespondError?.();
            const message = messageOf(error);
            if (reasonModalOpen) {
              setReasonModalError(message);
            } else {
              setActionError(message);
            }
          }
        );
      });
    });
  };
  const actions: ChainActions = {
    saving,
    nowMs,
    steps,
    catalog,
    reasonText,
    setReasonText,
    saveReason,
    openReasonModal,
    undo,
    respondTo,
    readOnly,
  };
  return {
    shown,
    decision,
    checks,
    steps,
    actions,
    actionError,
    pendingReasonStep,
    reasonModalOpen,
    reasonModalError,
    reasonDraft:
      pendingReasonStep != null
        ? draftReason(reasonText, pendingReasonStep)
        : "",
    closeReasonModal,
    onReasonDraft,
  };
};
